import React, { useState } from "react";
import { message, Modal, Button, Input, Spin, Card, Divider } from "antd";
import { useOutletContext } from "react-router-dom";
import { Building, MapPin } from "lucide-react";
import userService from "../../../../services/userService";
import { useLanguage } from "../../../../context/LanguageContext";
import dashboardContent from "../../../../utils/dashboardContent";

const DashboardProfile = () => {
  const { user } = useOutletContext();
  const { language } = useLanguage();
  const text = dashboardContent[language].profile;

  const [bio, setBio] = useState(
    "Enthusiastischer Entwickler mit einer Leidenschaft für Webtechnologien und Künstliche Intelligenz."
  );

  const [emailModalVisible, setEmailModalVisible] = useState(false);
  const [passwordModalVisible, setPasswordModalVisible] = useState(false);

  const [newEmail, setNewEmail] = useState(user.email);
  const [confirmNewEmail, setConfirmNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");

  const [loadingEmail, setLoadingEmail] = useState(false);
  const [loadingPassword, setLoadingPassword] = useState(false);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        // Hier könntest du den neuen Bild-URL speichern oder einen API-Aufruf machen
      };
      reader.readAsDataURL(file);
    }
  };

  const handleBioChange = (e) => {
    setBio(e.target.value);
  };

  const handleProfileSave = async () => {
    try {
      const response = await userService.updateProfile({ bio });
      console.log("Profile updated:", response);
    } catch (error) {
      console.error("Fehler beim Aktualisieren des Profils:", error.message);
    }
  };

  const handleEmailSave = async () => {
    if (newEmail !== confirmNewEmail) {
      message.error(text.emailError);
      return;
    }

    setLoadingEmail(true);
    try {
      await userService.changeEmail(newEmail);
      setEmailModalVisible(false);
    } catch (error) {
      console.error("Fehler beim Ändern der E-Mail:", error.message);
    } finally {
      setLoadingEmail(false);
    }
  };

  const handlePasswordSave = async () => {
    if (newPassword !== confirmNewPassword) {
      message.error(text.passwordError);
      return;
    }

    setLoadingPassword(true);
    try {
      await userService.changePassword(newPassword);
      setPasswordModalVisible(false);
      setNewPassword("");
      setConfirmNewPassword("");
    } catch (error) {
      console.error("Fehler beim Ändern des Passworts:", error.message);
    } finally {
      setLoadingPassword(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 py-10 px-6">
      <div className="max-w-5xl mx-auto bg-white shadow-lg rounded-lg p-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-8">
          {text.profile}
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 md:gap-8">
          {/* Profilbild */}
          <div className="col-span-1 flex flex-col items-center">
            <div className="relative w-40 h-40 rounded-full overflow-hidden border-4 border-gray-300 shadow-md">
              <img
                src={user.profilePicture || "https://via.placeholder.com/150"}
                alt="Profile"
                className="object-cover w-full h-full"
              />
              <label
                htmlFor="profileImageUpload"
                className="absolute bottom-0 right-0 bg-orange-500 text-white p-2 rounded-full cursor-pointer shadow-lg"
              >
                ✏️
              </label>
              <input
                type="file"
                id="profileImageUpload"
                className="hidden"
                accept="image/*"
                onChange={handleImageChange}
              />
            </div>
            <p className="mt-3 text-sm text-gray-500">{text.profileImage}</p>
          </div>

          {/* Benutzerinformationen */}
          <div className="col-span-2 space-y-6">
            {/* Kontotyp Badge */}
            <div className="flex items-center gap-2">
              {user.isCompany ? (
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 text-sm font-medium rounded-full">
                  <Building size={16} />
                  {language === "de" ? "Firmenaccount" : "Company Account"}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 text-sm font-medium rounded-full">
                  {language === "de" ? "Privataccount" : "Personal Account"}
                </span>
              )}
            </div>

            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                {text.nameLabel}
              </label>
              <p className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2">
                {user.name}
              </p>
            </div>

            {/* Firmenname - nur bei Firmen */}
            {user.isCompany && user.companyName && (
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  {language === "de" ? "Firmenname" : "Company Name"}
                </label>
                <p className="mt-1 w-full border border-gray-300 rounded-md shadow-sm p-2 flex items-center gap-2">
                  <Building size={16} className="text-gray-400" />
                  {user.companyName}
                </p>
              </div>
            )}

            {/* E-Mail */}
            <div className="flex flex-col md:flex-row items-center justify-between">
              <div className="w-full">
                <label className="block text-sm font-medium text-gray-700">
                  {text.emailLabel}
                </label>
                <p className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2">
                  {user.email}
                </p>
              </div>
              <Button
                onClick={() => setEmailModalVisible(true)}
                className="w-full md:w-max md:ml-4 mt-6 bg-orange-500 hover:bg-orange-600 text-white"
              >
                {text.changeEmail}
              </Button>
            </div>

            {/* Passwort */}
            <div className="flex flex-col md:flex-row items-center justify-between">
              <div className="w-full">
                <label className="block text-sm font-medium text-gray-700">
                  {text.passwordLabel}
                </label>
                <p className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2">
                  ********
                </p>
              </div>
              <Button
                onClick={() => setPasswordModalVisible(true)}
                className="w-full md:w-max md:ml-4 mt-6 bg-orange-500 hover:bg-orange-600 text-white"
              >
                {text.changePassword}
              </Button>
            </div>
          </div>
        </div>

        {/* Rechnungsadresse - nur bei Firmen */}
        {user.isCompany && user.billingAddress && (
          <>
            <Divider />
            <Card
              title={
                <div className="flex items-center gap-2">
                  <MapPin size={20} />
                  {language === "de" ? "Rechnungsadresse" : "Billing Address"}
                </div>
              }
              className="mt-6"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {language === "de" ? "Straße" : "Street"}
                  </label>
                  <p className="border border-gray-300 rounded-md p-2 bg-gray-50">
                    {user.billingAddress.street}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {language === "de" ? "Stadt" : "City"}
                  </label>
                  <p className="border border-gray-300 rounded-md p-2 bg-gray-50">
                    {user.billingAddress.postalCode} {user.billingAddress.city}
                  </p>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {language === "de" ? "Land" : "Country"}
                  </label>
                  <p className="border border-gray-300 rounded-md p-2 bg-gray-50">
                    {user.billingAddress.country}
                  </p>
                </div>
              </div>
            </Card>
          </>
        )}

        {/* Speichern-Button */}
        <div className="mt-8 text-center md:text-right">
          <button
            onClick={handleProfileSave}
            className="bg-orange-500 hover:bg-orange-600 text-white py-2 px-6 rounded-md shadow-sm"
          >
            {text.saveChanges}
          </button>
        </div>
      </div>

      {/* Modal für E-Mail-Änderung */}
      <Modal
        title={text.emailChangeTitle}
        open={emailModalVisible}
        onOk={handleEmailSave}
        onCancel={() => setEmailModalVisible(false)}
        okText={loadingEmail ? <Spin /> : text.save}
        cancelText={text.cancel}
        okButtonProps={{
          disabled: loadingEmail,
          style: { backgroundColor: "#fa8c16", borderColor: "#fa8c16" },
        }}
      >
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700">
            {text.newEmail}
          </label>
          <Input
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            className="mt-1"
          />
        </div>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700">
            {text.confirmNewEmail}
          </label>
          <Input
            type="email"
            value={confirmNewEmail}
            onChange={(e) => setConfirmNewEmail(e.target.value)}
            className="mt-1"
          />
        </div>
      </Modal>

      {/* Modal für Passwort-Änderung */}
      <Modal
        title={text.passwordChangeTitle}
        open={passwordModalVisible}
        onOk={handlePasswordSave}
        onCancel={() => setPasswordModalVisible(false)}
        okText={loadingPassword ? <Spin /> : text.save}
        cancelText={text.cancel}
        okButtonProps={{
          disabled: loadingPassword,
          style: { backgroundColor: "#fa8c16", borderColor: "#fa8c16" },
        }}
      >
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700">
            {text.newPassword}
          </label>
          <Input.Password
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className="mt-1"
          />
        </div>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700">
            {text.confirmNewPassword}
          </label>
          <Input.Password
            value={confirmNewPassword}
            onChange={(e) => setConfirmNewPassword(e.target.value)}
            className="mt-1"
          />
        </div>
      </Modal>
    </div>
  );
};

export default DashboardProfile;
